import React, { useState, useEffect, useRef } from 'react'
import {
  PdfViewerComponent,
  Magnification,
  Navigation,
  LinkAnnotation,
  BookmarkView,
  Annotation,
  FormFields,
  FormDesigner,
  ThumbnailView,
  Print,
  TextSelection,
  TextSearch,
  PageOrganizer,
  Inject,
} from '@syncfusion/ej2-react-pdfviewer'
import {
  ToolbarComponent,
  ItemsDirective,
  ItemDirective,
} from '@syncfusion/ej2-react-navigations'
import { useMediaQuery } from '@material-ui/core'

import './pdf-viewer.css'

/**
 * @typedef {object} Props
 * @prop {string} url Sets the PDF document URL.
 * @prop {Function} handleDocumentLoadFailed Custom function to handle when document cannot load.
 * @prop {import('i18next').TFunction} t Translation function
 * @prop {boolean} isExtended Handles a viewer greater height
 * @prop {string} customHeight Optional custom height for the viewer (overrides default heights)
 */

/**
 * @author Marissa Vargas
 * @date 2025-05-15
 * @description Component to view pdf's from SyncFusion, with a customized toolbar. Enables to navigate among pages, zoom, drag, select text and perfomr text search. It dynamically changes the tools for devices below 820 pexels.
 * @param {Props}
 * @returns {JSX.Element}
 * @example
 * ```jsx
 * <PdfViewer>
 *   url={'https://cdn.syncfusion.com/content/pdf/pdf-succinctly.pdf'}
 *   handleDocumentLoadFailed={(e) => setErrorMessage(e.message)}
 *   t={t}
 * </PdfViewer/>
 * ```
 */
function PdfViewer({ url, handleDocumentLoadFailed, t, isExtended, customHeight }) {
  const viewerRef = useRef(null)
  const toolbarRef = useRef(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isZooming, setIsZooming] = useState(false)

  const matchCaseRef = useRef(false)
  const searchTextRef = useRef('')
  const prevMatchCaseRef = useRef(false)
  const currentPageNumberRef = useRef('1')
  const isInkEnabledRef = useRef(false)
  const searchActiveRef = useRef(false)
  const viewerIdRef = useRef(`pdfviewer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`)

  const is1024 = useMediaQuery('(max-width:1024px)')
  const is1820 = useMediaQuery('(min-width: 1820px)')
  const is1366 = useMediaQuery('(min-width: 1366px)')

  let viewerHeight = '100%'

  if (customHeight) {
    viewerHeight = customHeight
  } else if (isExtended) {
    if (is1820) {
      viewerHeight = '788px'
    } else if (is1366) {
      viewerHeight = '788px'
    } else if (is1024) {
      viewerHeight = '1140px'
    }
  }
  useEffect(() => {
    if (!isLoading && !hasError) {
      const timer = setTimeout(() => {
        const viewer = viewerRef.current
        if (!viewer || !viewer.pageCount) return

        requestAnimationFrame(() => {
          const currentPageInput = document.getElementById(`currentPage_${viewerIdRef.current}`)
          const totalPageElement = document.getElementById(`totalPage_${viewerIdRef.current}`)

          if (currentPageInput && viewer.currentPageNumber) {
            const newValue = viewer.currentPageNumber.toString()
            if (currentPageInput.value !== newValue) {
              currentPageInput.value = newValue
              currentPageNumberRef.current = newValue
            }
          }

          if (totalPageElement && viewer.pageCount) {
            const newText = `${t('of')} ` + viewer.pageCount
            if (totalPageElement.textContent !== newText) {
              totalPageElement.textContent = newText
            }
          }

          if (toolbarRef.current && viewer.pageCount) {
            try {
              const previousPageElement = document.getElementById('previous_page')
              const nextPageElement = document.getElementById('next_page')

              if (previousPageElement && nextPageElement) {
                if (viewer.currentPageNumber === 1) {
                  toolbarRef.current.enableItems(previousPageElement.parentElement, false)
                  toolbarRef.current.enableItems(nextPageElement.parentElement, true)
                } else if (viewer.currentPageNumber === viewer.pageCount) {
                  toolbarRef.current.enableItems(previousPageElement.parentElement, true)
                  toolbarRef.current.enableItems(nextPageElement.parentElement, false)
                } else {
                  toolbarRef.current.enableItems(previousPageElement.parentElement, true)
                  toolbarRef.current.enableItems(nextPageElement.parentElement, true)
                }
              }
            } catch (error) {
              console.log('Error updating page navigation:', error)
            }
          }
        })
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [isLoading, hasError, t])

  function template() {
    return (
      <div style={{ margin: '0px 6px' }}>
        <span className='e-pv-total-page-number' id={`totalPage_${viewerIdRef.current}`}>
          of 0
        </span>
      </div>
    )
  }

  const handleCreated = () => {
    const viewer = viewerRef.current
    if (viewer) {
      viewer.dataBind()
      viewer.load(url, null)
      viewer.serviceUrl = ''
    }
  }

  const handleDocumentLoad = () => {
    setIsLoading(false)
    setHasError(false)
    documentLoaded()
    updatePageNumbers()
  }

  const handleDocumentLoadError = (args) => {
    console.error('Document load failed:', args)
    setIsLoading(false)
    setHasError(true)
    setErrorMessage(args.message || t('pdfViewer.error.cannotLoad'))

    if (handleDocumentLoadFailed) {
      handleDocumentLoadFailed(args)
    }
  }

  const updatePageNumbers = () => {
    const viewer = viewerRef.current
    if (!viewer || !viewer.pageCount) return

    requestAnimationFrame(() => {
      const currentPageInput = document.getElementById(`currentPage_${viewerIdRef.current}`)
      const totalPageElement = document.getElementById(`totalPage_${viewerIdRef.current}`)

      if (currentPageInput && viewer.currentPageNumber) {
        const newValue = viewer.currentPageNumber.toString()
        if (currentPageInput.value !== newValue) {
          currentPageInput.value = newValue
          currentPageNumberRef.current = newValue
        }
      }

      if (totalPageElement && viewer.pageCount) {
        const newText = `${t('of')} ` + viewer.pageCount
        if (totalPageElement.textContent !== newText) {
          totalPageElement.textContent = newText
        }
      }

      updatePageNavigation()
    })
  }

  const handleZoomChange = () => {
    updatePageNumbers()
  }

  function inputTemplate() {
    return (
      <div>
        <input
          type='text'
          className='e-input-group e-pv-current-page-number'
          id={`currentPage_${viewerIdRef.current}`}
        />
      </div>
    )
  }

  return (
    <div>
      {is1024 ? (
        <div className='control-section'>
          <div>
            <div className='e-pdf-toolbar'>
              <ToolbarComponent
                ref={toolbarRef}
                clicked={clickHandler.bind(this)}
              >
                <ItemsDirective>
                  <ItemDirective
                    prefixIcon='e-icons e-first-page'
                    id='first_page'
                    tooltipText={t('pdfViewer.tooltip.firstPage')}
                    align='Left'
                  />
                  <ItemDirective
                    prefixIcon='e-icons e-chevron-left'
                    id='previous_page'
                    tooltipText={t('pdfViewer.tooltip.previousPage')}
                    align='Left'
                  />
                  <ItemDirective
                    prefixIcon='e-icons e-chevron-right'
                    id='next_page'
                    tooltipText={t('pdfViewer.tooltip.nextPage')}
                    align='Left'
                  />
                  <ItemDirective
                    prefixIcon='e-icons e-last-page'
                    id='last_page'
                    tooltipText={t('pdfViewer.tooltip.lastPage')}
                    align='Left'
                  />
                  <ItemDirective
                    template={inputTemplate}
                    tooltipText={t('pdfViewer.tooltip.pageNumber')}
                    type='Input'
                    align='Left'
                  />
                  <ItemDirective
                    template={template}
                    align='Left'
                    tooltipText={t('pdfViewer.tooltip.pageNumber')}
                  />

                  <ItemDirective type='Separator' />
                  <ItemDirective
                    prefixIcon='e-icons e-pan'
                    id='pan_tool'
                    tooltipText={t('pdfViewer.tooltip.panMode')}
                  />

                  <ItemDirective type='Separator' align='Center' />
                  <ItemDirective
                    prefixIcon='e-icons e-search'
                    tooltipText={t('pdfViewer.tooltip.findText')}
                    id='find_text'
                    align='Right'
                  />
                </ItemsDirective>
              </ToolbarComponent>
            </div>

            <div
              id='textSearchToolbar'
              style={{ display: 'none', marginLeft: '840px' }}
            >
              <div
                className='e-pv-search-bar'
                id='container_search_box'
              >
                <div
                  className='e-pv-search-bar-elements'
                  id='container_search_box_elements'
                >
                  <div
                    className='e-input-group e-pv-search-input'
                    id='container_search_input_container'
                  >
                    <input
                      className='e-input'
                      id='container_search_input'
                      type='text'
                      placeholder={t('pdfViewer.searchText.findInDocument')}
                      onKeyPress={searchInputKeypressed}
                      onChange={inputChange}
                    />
                    <span
                      className='e-input-group-icon e-input-search-group-icon e-icons e-search'
                      id='container_search_box-icon'
                      onClick={searchClickHandler}
                    ></span>
                    <span
                      className='e-pv-search-count-label'
                      id='container_search_count_label'
                    ></span>
                  </div>
                  <button
                    className='e-btn e-icon-btn e-pv-search-btn e-icons e-chevron-left'
                    id='container_prev_occurrence'
                    type='button'
                    disabled
                    aria-label='Previous Search text'
                  >
                    <span
                      className='e-pv-icon-search e-pv-prev-search-icon'
                      id='container_prev_occurrenceIcon'
                      onClick={previousTextSearch}
                    ></span>
                  </button>
                  <button
                    className='e-btn e-icon-btn e-pv-search-btn e-icons e-chevron-right'
                    id='container_next_occurrence'
                    type='button'
                    disabled
                    aria-label='Next Search text'
                  >
                    <span
                      className='e-pv-icon-search e-pv-next-search-icon'
                      id='container_next_occurrenceIcon'
                      onClick={nextTextSearch}
                    ></span>
                  </button>
                </div>
                <div
                  className='e-pv-match-case-container'
                  id='container_match_case_container'
                >
                  <div className='e-checkbox-wrapper e-wrapper e-pv-match-case'>
                    <label htmlFor='container_match_case'>
                      <input
                        id='container_match_case'
                        type='checkbox'
                        className='e-control e-checkbox e-lib'
                        onClick={checkBoxChanged}
                      />
                      <span
                        className='e-ripple-container'
                        data-ripple='true'
                      ></span>
                      <span id='checkboxSpan' className='e-icons e-frame'></span>
                      <span className='e-label'>
                        {t('pdfViewer.checkBox.matchCase')}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            {/* Render the PDF Viewer */}
            <PdfViewerComponent
              id={`container_${viewerIdRef.current}`}
              ref={viewerRef}
              created={handleCreated}
              enableToolbar={false}
              enableNavigationToolbar={false}
              enableAnnotationToolbar={false}
              enableCommentPanel={false}
              documentLoad={handleDocumentLoad}
              documentLoadFailed={handleDocumentLoadError}
              pageChange={onPageChange}
              resourceUrl={`${process.env.REACT_APP_RESOURCE_URL_PDF_VIEWER_SYNCFUSION}`}
              documentPath={url}
              style={{
                display: 'block',
                height: viewerHeight,
                touchAction: 'pan-x pan-y',
              }}            >
              <Inject
                services={[
                  Magnification,
                  Navigation,
                  LinkAnnotation,
                  BookmarkView,
                  FormFields,
                  FormDesigner,
                  PageOrganizer,
                  ThumbnailView,
                  Print,
                  TextSelection,
                  TextSearch,
                  Annotation,
                ]}
              />
            </PdfViewerComponent>
          </div>
        </div>
      ) : (
        <div className='control-section'>
          <div>
            <div className='e-pdf-toolbar'>
              <ToolbarComponent
                ref={toolbarRef}
                clicked={clickHandler.bind(this)}
              >
                <ItemsDirective>
                  <ItemDirective
                    prefixIcon='e-icons e-first-page'
                    id='first_page'
                    tooltipText={t('pdfViewer.tooltip.firstPage')}
                    align='Left'
                  />
                  <ItemDirective
                    prefixIcon='e-icons e-chevron-left'
                    id='previous_page'
                    tooltipText={t('pdfViewer.tooltip.previousPage')}
                    align='Left'
                  />
                  <ItemDirective
                    prefixIcon='e-icons e-chevron-right'
                    id='next_page'
                    tooltipText={t('pdfViewer.tooltip.nextPage')}
                    align='Left'
                  />
                  <ItemDirective
                    prefixIcon='e-icons e-last-page'
                    id='last_page'
                    tooltipText={t('pdfViewer.tooltip.lastPage')}
                    align='Left'
                  />
                  <ItemDirective
                    template={inputTemplate}
                    tooltipText={t('pdfViewer.tooltip.pageNumber')}
                    type='Input'
                    align='Left'
                  />
                  <ItemDirective
                    template={template}
                    align='Left'
                    tooltipText={t('pdfViewer.tooltip.pageNumber')}
                  />

                  <ItemDirective type='Separator' align='Left' />
                  <ItemDirective
                    prefixIcon='e-icons e-circle-add'
                    id='zoom_in'
                    tooltipText={t('pdfViewer.tooltip.zoomIn')}
                  />
                  <ItemDirective
                    prefixIcon='e-icons e-circle-remove'
                    id='zoom_out'
                    tooltipText={t('pdfViewer.tooltip.zoomOut')}
                  />
                  <ItemDirective type='Separator' />

                  <ItemDirective
                    prefixIcon='e-icons e-mouse-pointer'
                    id='text_selection_tool'
                    tooltipText={t('pdfViewer.tooltip.textSelect')}
                  />
                  <ItemDirective
                    prefixIcon='e-icons e-pan'
                    id='pan_tool'
                    tooltipText={t('pdfViewer.tooltip.panMode')}
                  />
                  <ItemDirective type='Separator' />

                  <ItemDirective type='Separator' align='Center' />
                  <ItemDirective
                    prefixIcon='e-icons e-search'
                    tooltipText={t('pdfViewer.tooltip.findText')}
                    id='find_text'
                    align='Right'
                  />
                </ItemsDirective>
              </ToolbarComponent>
            </div>

            <div
              id='textSearchToolbar'
              style={{ display: 'none', marginLeft: '840px' }}
            >
              <div
                className='e-pv-search-bar'
                id='container_search_box'
              >
                <div
                  className='e-pv-search-bar-elements'
                  id='container_search_box_elements'
                >
                  <div
                    className='e-input-group e-pv-search-input'
                    id='container_search_input_container'
                  >
                    <input
                      className='e-input'
                      id='container_search_input'
                      type='text'
                      placeholder={t('pdfViewer.searchText.findInDocument')}
                      onKeyPress={searchInputKeypressed}
                      onChange={inputChange}
                    />


                    <span
                      className='e-input-group-icon e-input-search-group-icon e-icons e-search'
                      id='container_search_box-icon'
                      onClick={searchClickHandler}
                    ></span>

                    <span
                      className='e-pv-search-count-label'
                      id='container_search_count_label'

                    ></span>
                  </div>
                  <button
                    className='e-btn e-icon-btn e-pv-search-btn e-icons e-chevron-left'
                    id='container_prev_occurrence'
                    type='button'
                    disabled
                    aria-label='Previous Search text'
                  >
                    <span
                      className='e-pv-icon-search e-pv-prev-search-icon'
                      id='container_prev_occurrenceIcon'
                      onClick={previousTextSearch}
                    ></span>
                  </button>
                  <button
                    className='e-btn e-icon-btn e-pv-search-btn e-icons e-chevron-right'
                    id='container_next_occurrence'
                    type='button'
                    disabled
                    aria-label='Next Search text'
                  >
                    <span
                      className='e-pv-icon-search e-pv-next-search-icon'
                      id='container_next_occurrenceIcon'
                      onClick={nextTextSearch}
                    ></span>
                  </button>
                </div>
                <div
                  className='e-pv-match-case-container'
                  id='container_match_case_container'
                >
                  <div className='e-checkbox-wrapper e-wrapper e-pv-match-case'>
                    <label htmlFor='container_match_case'>
                      <input
                        id='container_match_case'
                        type='checkbox'
                        className='e-control e-checkbox e-lib'
                        onClick={checkBoxChanged}
                      />
                      <span
                        className='e-ripple-container'
                        data-ripple='true'
                      ></span>
                      <span id='checkboxSpan' className='e-icons e-frame'></span>
                      <span className='e-label'>
                        {t('pdfViewer.checkBox.matchCase')}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            {/* Render the PDF Viewer */}
            <PdfViewerComponent
              id={`container_${viewerIdRef.current}`}
              ref={viewerRef}
              enableToolbar={false}
              enableNavigationToolbar={false}
              enableAnnotationToolbar={false}
              enableCommentPanel={false}
              documentLoad={handleDocumentLoad}
              created={handleCreated}
              documentLoadFailed={handleDocumentLoadError}
              pageChange={onPageChange}
              resourceUrl={`${process.env.REACT_APP_RESOURCE_URL_PDF_VIEWER_SYNCFUSION}`}
              documentPath={url}
              style={{
                display: 'block',
                height: viewerHeight,
                touchAction: 'pan-x pan-y',
              }}            >
              <Inject
                services={[
                  Magnification,
                  Navigation,
                  LinkAnnotation,
                  BookmarkView,
                  FormFields,
                  FormDesigner,
                  PageOrganizer,
                  ThumbnailView,
                  Print,
                  TextSelection,
                  TextSearch,
                  Annotation,
                ]}
              />
            </PdfViewerComponent>
          </div>
        </div>
      )}

    </div>
  )

  function onPageChange() {
    const viewer = viewerRef.current
    if (!viewer || !viewer.pageCount) return

    currentPageNumberRef.current = viewer.currentPageNumber.toString()
    let inputElement = document.getElementById(`currentPage_${viewerIdRef.current}`)
    if (inputElement) {
      inputElement.value = currentPageNumberRef.current
    }
    updatePageNavigation()
    updatePageNumbers()
  }
  function documentLoaded() {
    const viewer = viewerRef.current
    if (!viewer || !viewer.pageCount) return

    document.addEventListener('click', checkSearchActive.bind(this))

    setTimeout(() => {
      var pageCount = document.getElementById(`totalPage_${viewerIdRef.current}`)
      if (pageCount) {
        pageCount.textContent = `${t('of')} ` + viewer.pageCount
      }

      let inputElement = document.getElementById(`currentPage_${viewerIdRef.current}`)
      if (inputElement) {
        inputElement.addEventListener('click', currentPageClicked.bind(this))
        inputElement.addEventListener(
          'keypress',
          onCurrentPageBoxKeypress.bind(this)
        )
        inputElement.value = '1'
      }
      updatePageNavigation()
      updatePageNumbers()
    }, 100)
  }
  function updatePageNavigation() {
    const viewer = viewerRef.current
    const toolbar = toolbarRef.current
    if (!viewer || !viewer.pageCount || !toolbar) return

    try {
      const previousPageElement = document.getElementById('previous_page')
      const nextPageElement = document.getElementById('next_page')

      if (!previousPageElement || !nextPageElement) return

      if (viewer.currentPageNumber === 1) {
        toolbar.enableItems(previousPageElement.parentElement, false)
        toolbar.enableItems(nextPageElement.parentElement, true)
      } else if (viewer.currentPageNumber === viewer.pageCount) {
        toolbar.enableItems(previousPageElement.parentElement, true)
        toolbar.enableItems(nextPageElement.parentElement, false)
      } else {
        toolbar.enableItems(previousPageElement.parentElement, true)
        toolbar.enableItems(nextPageElement.parentElement, true)
      }
    } catch (error) {
      console.warn('Error updating page navigation:', error)
    }
  }

  function onCurrentPageBoxKeypress(event) {
    const viewer = viewerRef.current
    if (!viewer || !viewer.pageCount || !viewer.navigation) return true

    let currentPageBox = document.getElementById(`currentPage_${viewerIdRef.current}`)
    if (!currentPageBox) return true

    if (
      (event.which < 48 || event.which > 57) &&
      event.which !== 8 &&
      event.which !== 13
    ) {
      event.preventDefault()
      return false
    } else {
      var currentPageNumber = parseInt(currentPageBox.value)
      if (event.which === 13) {
        try {
          if (currentPageNumber > 0 && currentPageNumber <= viewer.pageCount) {
            viewer.navigation.goToPage(currentPageNumber)
          } else {
            currentPageBox.value = viewer.currentPageNumber.toString()
          }
        } catch (error) {
          console.warn('Error navigating to page:', error)
          currentPageBox.value = viewer.currentPageNumber.toString()
        }
      }
      return true
    }
  }

  function currentPageClicked() {
    let currentPage = document.getElementById(`currentPage_${viewerIdRef.current}`)
    if (currentPage) {
      currentPage.select()
    }
  }
  function searchInputKeypressed(event) {
    if (event.key === 'Enter') {
      initiateTextSearch()
    }
  }

  function initiateTextSearch() {
    const viewer = viewerRef.current
    if (!viewer) return
    const textsearchPrevElement = document.getElementById(
      'container_prev_occurrence'
    )
    const textsearchNextElement = document.getElementById(
      'container_next_occurrence'
    )
    var textsearchElement = document.getElementById(
      'container_search_box-icon'
    )
    if (textsearchNextElement && textsearchPrevElement && textsearchElement) {
      textsearchPrevElement.disabled = false
      textsearchNextElement.disabled = false
      textsearchElement.classList.add('e-close')
      textsearchElement.classList.remove('e-search')
      textsearchPrevElement.addEventListener('click', previousTextSearch)
      textsearchNextElement.addEventListener('click', nextTextSearch)
      if (
        searchTextRef.current !==
        document.getElementById('container_search_input').value ||
        prevMatchCaseRef.current !== matchCaseRef.current
      ) {
        viewer.textSearch.cancelTextSearch()
        searchTextRef.current = document.getElementById('container_search_input').value
        searchActiveRef.current = true
        viewer.textSearch.searchText(searchTextRef.current, matchCaseRef.current)
        prevMatchCaseRef.current = matchCaseRef.current
      } else {
        nextTextSearch()
      }
    }
  }

  function checkSearchActive() {
    const viewer = viewerRef.current
    if (viewer && viewer.textSearchModule && !searchActiveRef.current) {
      viewer.textSearchModule.clearAllOccurrences()
    }
  }
  function inputChange() {
    const viewer = viewerRef.current
    if (!viewer) return

    viewer.textSearchModule.clearAllOccurrences()
    searchActiveRef.current = false
    if (document.getElementById('container_search_input').value === '') {
      updateSearchInputIcon(true)
      viewer.textSearch.cancelTextSearch()
      searchTextRef.current = ''
    }
  }
  function searchClickHandler() {
    const viewer = viewerRef.current
    if (!viewer) return

    var searchBtn = document.getElementById('container_search_box-icon')
    if (searchBtn.classList.contains('e-search')) {
      viewer.textSearch.cancelTextSearch()
      initiateTextSearch()
      updateSearchInputIcon(false)
      searchTextRef.current = ''
    } else if (searchBtn.classList.contains('e-close')) {
      var searchInput = document.getElementById('container_search_input')
      updateSearchInputIcon(true)
      searchInput.value = ''
      searchInput.focus()
      viewer.textSearch.cancelTextSearch()
      searchTextRef.current = ''
    }
  }

  function updateSearchInputIcon(isEnable) {
    var searchBtn = document.getElementById('container_search_box-icon')
    if (isEnable) {
      searchBtn.classList.add('e-search')
      searchBtn.classList.remove('e-close')
    } else {
      searchBtn.classList.add('e-close')
      searchBtn.classList.remove('e-search')
    }
  }

  function nextTextSearch() {
    const viewer = viewerRef.current
    if (!viewer) return

    disableInkAnnotation()
    viewer.textSearchModule.searchNext()
    searchActiveRef.current = true
  }

  function previousTextSearch() {
    const viewer = viewerRef.current
    if (!viewer) return

    disableInkAnnotation()
    viewer.textSearchModule.searchPrevious()
    searchActiveRef.current = true
  }

  function disableInkAnnotation() {
    const viewer = viewerRef.current
    if (!viewer) return

    if (isInkEnabledRef.current) {
      viewer.annotation.setAnnotationMode('None')
      isInkEnabledRef.current = false
    }
  }

  function checkBoxChanged(event) {
    const target = event.target
    if (target.checked) {
      const matchcaseElement = document.getElementById('container_match_case')
      if (matchcaseElement) {
        matchcaseElement.checked = true
      }
      matchCaseRef.current = true
      const checkboxSpanElement = document.getElementById('checkboxSpan')
      if (checkboxSpanElement) {
        checkboxSpanElement.classList.add('e-check')
      }
    } else {
      matchCaseRef.current = false
      const checkboxSpanElement = document.getElementById('checkboxSpan')
      if (checkboxSpanElement) {
        checkboxSpanElement.classList.remove('e-check')
      }
    }
  }
  function clickHandler(args) {
    const viewer = viewerRef.current
    if (!viewer || !viewer.pageCount || !viewer.navigation) return

    switch (args.item.id) {
      case 'previous_page':
        try {
          if (viewer.currentPageNumber > 1) {
            disableInkAnnotation()
            viewer.navigation.goToPreviousPage()
          }
        } catch (error) {
          console.warn('Error navigating to previous page:', error)
        }
        break
      case 'next_page':
        try {
          if (viewer.currentPageNumber < viewer.pageCount) {
            disableInkAnnotation()
            viewer.navigation.goToNextPage()
          }
        } catch (error) {
          console.warn('Error navigating to next page:', error)
        }
        break
      case 'first_page':
        try {
          disableInkAnnotation()
          viewer.navigation.goToPage(1)
          const currentPageInput = document.getElementById(`currentPage_${viewerIdRef.current}`)
          if (currentPageInput) {
            currentPageInput.value = '1'
          }
        } catch (error) {
          console.warn('Error navigating to first page:', error)
        }
        break
      case 'last_page':
        try {
          disableInkAnnotation()
          viewer.navigation.goToPage(viewer.pageCount)
          const currentPageInput = document.getElementById(`currentPage_${viewerIdRef.current}`)
          if (currentPageInput) {
            currentPageInput.value = viewer.pageCount.toString()
          }
        } catch (error) {
          console.warn('Error navigating to last page:', error)
        }
        break
      case 'zoom_in':
        {
          disableInkAnnotation()
          viewer.magnification.zoomIn()
          let textSearchToolbarElement =
            document.getElementById('textSearchToolbar')
          if (textSearchToolbarElement && textSearchToolbarElement.style.display === 'block')
            textSearchToolbarElement.style.display = 'none'
        }
        break
      case 'zoom_out':
        {
          disableInkAnnotation()
          viewer.magnification.zoomOut()
          let textSearchToolbarElement =
            document.getElementById('textSearchToolbar')
          if (textSearchToolbarElement && textSearchToolbarElement.style.display === 'block')
            textSearchToolbarElement.style.display = 'none'
        }
        break
      case 'text_selection_tool':
        {
          disableInkAnnotation()
          viewer.interactionMode = 'TextSelection'
          let textSearchToolbarElement =
            document.getElementById('textSearchToolbar')
          if (textSearchToolbarElement && textSearchToolbarElement.style.display === 'block')
            textSearchToolbarElement.style.display = 'none'
        }
        break
      case 'pan_tool':
        {
          disableInkAnnotation()
          viewer.interactionMode = 'Pan'
          let textSearchToolbarElement =
            document.getElementById('textSearchToolbar')
          if (textSearchToolbarElement && textSearchToolbarElement.style.display === 'block')
            textSearchToolbarElement.style.display = 'none'
        }
        break
      case 'find_text': {
        disableInkAnnotation()
        let textSearchToolbarElement =
          document.getElementById('textSearchToolbar')
        if (textSearchToolbarElement) {
          if (textSearchToolbarElement.style.display === 'block')
            textSearchToolbarElement.style.display = 'none'
          else textSearchToolbarElement.style.display = 'block'
        }
        break
      }
      default:
        break
    }
  }
}

export default PdfViewer